/**
 * SSE client for `POST /chat`.
 *
 * `EventSource` is not usable here: it only issues GET requests, and the question belongs in a
 * body rather than in a URL that lands in every access log along the way. So the stream is read
 * from `fetch` with a `ReadableStream` and the SSE frames are parsed by hand.
 *
 * Two details that a naive reader gets wrong:
 *
 * - **Chunk boundaries do not align with frames.** A network chunk can end mid-frame, and a
 *   single chunk can carry several frames. So bytes accumulate in a buffer and only complete
 *   frames (terminated by a blank line) are dispatched.
 * - **A frame can carry several `data:` lines.** Per the SSE specification they are joined with
 *   newlines. Our payloads are single-line JSON today, but an answer containing a newline would
 *   otherwise truncate silently, and silent truncation of a banking answer is the worst failure
 *   mode available.
 */

import type { ChatStreamEvent, Language } from "./types";

export interface ChatStreamRequest {
  message: string;
  session_id?: string | null;
  language?: Language | null;
}

export interface ChatStreamOptions {
  /** Base URL of the API. Defaults to same-origin `/api`, which the dev server proxies. */
  baseUrl?: string;
  /** Aborts the stream — wired to the composer's stop button and to unmount. */
  signal?: AbortSignal;
  /** Employee id, development auth mode only. Production uses the SSO session cookie. */
  employeeId?: string;
}

export class ChatStreamError extends Error {
  /**
   * HTTP status, when the failure came from a response rather than from the transport. Declared
   * as `| undefined` rather than optional: it is always present on the instance, sometimes with
   * no value, and `exactOptionalPropertyTypes` distinguishes those correctly.
   */
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "ChatStreamError";
    this.status = status;
  }
}

const FRAME_SEPARATOR = /\r?\n\r?\n/;

/** Parse one SSE frame into a typed event, or null for a comment/keepalive frame. */
export function parseFrame(frame: string): ChatStreamEvent | null {
  let name: string | null = null;
  const dataLines: string[] = [];

  for (const rawLine of frame.split(/\r?\n/)) {
    if (rawLine.startsWith(":")) continue; // comment / keepalive
    if (rawLine.startsWith("event:")) {
      name = rawLine.slice("event:".length).trim();
    } else if (rawLine.startsWith("data:")) {
      dataLines.push(rawLine.slice("data:".length).replace(/^ /, ""));
    }
  }

  if (!name || dataLines.length === 0) return null;

  try {
    return { event: name, data: JSON.parse(dataLines.join("\n")) } as ChatStreamEvent;
  } catch {
    // A malformed frame is skipped rather than fatal: losing one delta degrades an answer,
    // aborting loses one the employee is already reading.
    return null;
  }
}

/**
 * Stream a chat turn, yielding typed events in order.
 *
 * The async generator shape means the caller writes a plain `for await` loop and cancellation is
 * just breaking out of it — no listener bookkeeping, and no chance of a handler firing after the
 * component has unmounted.
 */
export async function* streamChat(
  request: ChatStreamRequest,
  options: ChatStreamOptions = {},
): AsyncGenerator<ChatStreamEvent> {
  const baseUrl = options.baseUrl ?? "/api";

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (options.employeeId) {
    headers["X-NAB-Employee-Id"] = options.employeeId;
  }

  const response = await fetch(`${baseUrl}/chat`, {
    method: "POST",
    headers,
    body: JSON.stringify(request),
    // The SSO session cookie must travel with the request.
    credentials: "same-origin",
    signal: options.signal ?? null,
  });

  if (!response.ok) {
    // 503 is the audit store being unwritable, which is a distinct and meaningful state: the
    // turn did not happen. Surfaced with its status so the UI can say so specifically.
    let detail = `The assistant returned ${response.status}.`;
    try {
      const body = await response.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      /* non-JSON error body; the status is enough */
    }
    throw new ChatStreamError(detail, response.status);
  }

  if (!response.body) {
    throw new ChatStreamError("The response carried no stream.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // stream: true so a multi-byte UTF-8 character split across chunks is not mangled —
      // Arabic is multi-byte throughout, so this is the normal case here, not an edge case.
      buffer += decoder.decode(value, { stream: true });

      const frames = buffer.split(FRAME_SEPARATOR);
      // The last element is either empty or a partial frame; keep it for the next chunk.
      buffer = frames.pop() ?? "";

      for (const frame of frames) {
        const parsed = parseFrame(frame);
        if (parsed) yield parsed;
      }
    }

    const trailing = parseFrame(buffer + decoder.decode());
    if (trailing) yield trailing;
  } finally {
    // Releasing the lock lets the connection be torn down promptly on abort, rather than
    // lingering until GC.
    reader.releaseLock();
  }
}
